import type { ReactNode } from 'react';
import { memo } from 'react';
import { useCurrentUser, useHasPermissions } from 'stores/useUserStore';
import styled, { css } from 'styled-components';

import MultiNavLinkSubItems, { MultiNavLinkSubItemsContainer } from 'components/core/navigation/MultiNavLinkSubItems';
import { navItemSizeStyles, navItemStyles } from 'components/core/navigation/shared/CommonNavItems';
import { WrapLink } from 'components/core/navigation/shared/WrapLink';
import VisuallyHiddenText from 'components/core/typography/VisuallyHiddenText';
import { useRouter } from 'hooks/useRouter';

import type { ResourceType, UserScope } from 'store/client/graphql';
import { AccessLevel } from 'store/client/graphql';

import { BLUE_500, NEUTRAL_0, NEUTRAL_300, NEUTRAL_700, NEUTRAL_800, RED_500 } from 'styles/tokens';
const borderSize = '3px';
const NotificationBadge = styled.div`
  background: ${RED_500};
  border: ${borderSize} solid ${NEUTRAL_800};
  border-radius: 10px;
  bottom: -${borderSize};
  height: 13px;
  position: absolute;
  right: -${borderSize};
  width: 13px;
`;

const NavLinkItem = styled(WrapLink)<{ disabled: boolean }>`
  ${navItemStyles}
  ${navItemSizeStyles}
  background: ${NEUTRAL_700};
  color: ${NEUTRAL_300};
  position: relative;

  &.active {
    background: ${BLUE_500};
    color: ${NEUTRAL_0};
  }
  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
    `}
`;

const MultiNavLinkItem = styled.div`
  position: relative;

  &:hover ${MultiNavLinkSubItemsContainer} {
    display: block;
  }
`;

interface Props {
  /**
   * The accessLevel used when checking user permissions
   */
  accessLevel?: AccessLevel;
  /**
   * Whether to force disable the link
   */
  forceDisabled?: boolean;
  /**
   * Whether to display the notification badge
   */
  hasNotification?: boolean;
  /**
   * The icon to display
   */
  icon: ReactNode;
  /**
   * The resourceType used when checking user permissions
   */
  resourceType?: ResourceType;
  /**
   * The scope required to render this link
   */
  scope?: UserScope[];
  /**
   * The id for testing
   */
  testId?: string;
  /**
   * The title used for accessibility
   */
  title: string;
  /**
   * The `to` prop passed to NavLinkItem component
   */
  to: string;
}

const TitledNavLinkItem = ({
  accessLevel = AccessLevel.BASIC,
  forceDisabled,
  hasNotification,
  icon,
  resourceType,
  scope,
  testId,
  title,
  to,
}: Props) => {
  const canAccess = useHasPermissions(resourceType ? [{ level: accessLevel, resource: resourceType }] : null);
  const { scope: userScope } = useCurrentUser();
  const { pathname } = useRouter();

  return canAccess && (!scope || scope?.includes(userScope)) ? (
    <MultiNavLinkItem data-testid={testId}>
      <NavLinkItem disabled={forceDisabled || pathname.indexOf(to) === 0} to={to}>
        {hasNotification ? <NotificationBadge /> : null}
        <VisuallyHiddenText>{title}</VisuallyHiddenText>
        {icon}
      </NavLinkItem>
      <MultiNavLinkSubItems items={[{ label: title, url: to }]} />
    </MultiNavLinkItem>
  ) : null;
};

export default memo(TitledNavLinkItem);
